/**
 * Everything that edits an open draft, shared by the Composer and Composer Fast (moved out of ComposePage, Dax, 2026-10-31):
 * the draft and its saves, the audio panel and its levelling, the live mix, and the two-player preview. A screen brings its
 * own cards and tools and renders `audioDesk()`, `audioPopups()` and the players. A change here reaches every screen that
 * uses it, which is what a fix should do; where a screen needs to differ, it passes an option.
 */
import { useIsMutating, useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LEVEL_DECODE_MAX_MS, LAYER_CHANNELS, MUSIC_LEAD_PREROLL_MS, MUSIC_LEAD_SILENCE_MIN_MS, MUSIC_SIGNAL_FLOOR_DB, SFX_NUDGE_MAX_MS, SOUND_LEAD_PREROLL_MS, LEVEL_TARGET_DB, SFX_NUDGE_MIN_OVERLAP_MS, PEAKS_PER_SECOND, TRIM_MIN_MS, channelHeard, clampToClip, levelGainDb, nextSegmentAtMs, placeSound, segmentStretch, trimLeavesClip, CLIPPING_PEAK_DB, MASTER_GAIN_MAX_DB, MASTER_GAIN_MIN_DB, MASTER_LEVEL_SETTLE_MS, MASTER_PEAK_CEILING_DB, masterCorrection, masterLevelDb, type AudioChannel, type ChannelLevel, type DraftMutes, type DraftSlot, type DraftSolos, type LayerSegment } from '@studio/contracts';
import { createPortal } from 'react-dom';
import { Chevron, Icon, Spark } from '@/components/icons';
import { CutStrip, type TrimEdge } from '@/components/cut-strip';
import { Knob } from '@/components/knob';
import { LayerLane } from '@/components/layer-lane';
import { MusicPicker, SoundtrackPicker, VoicePicker, WHOLE_CLIP } from '@/components/mix-pickers';
import { Solo, Speaker } from '@/components/speaker';
import { Tool, Toolbar } from '@/components/toolbar';
import type { SaveState } from '@/hooks/use-autosave';
import { useDraftHistory } from '@/hooks/use-draft-history';
import { fmtTime } from '@/lib/format';
import { clipColours } from '@/lib/clip-hues';
import { clampSlip, clampSoundOffset, mixStems, planMix, slotAt, type MixDraft } from '@/lib/mix-plan';
import { EDGE_INSET_MS, frameAgainstEdge, insideEdge } from '@/lib/playback';
import { mixUrl, PreviewMix } from '@/lib/preview-mix';
import { queryClient, trpc } from '@/lib/trpc';

export const GAIN_MIN = -24;
export const GAIN_MAX = 6;
export const EXPORT_POLL_MS = 1500;
/** How often the desk's meters read the live mix while the preview plays. */
export const METER_POLL_MS = 250;
/** A playhead parked this close to the end starts the cut again from the top. */
export const PLAYHEAD_END_RESTART_MS = 250;
export type AskChannel = 'soundtrack' | 'music' | 'voice' | 'clips' | 'asmr' | 'master';
/** Nobody soloed: a solo is someone listening, so the master levels the mix as it will play. */
export const NO_SOLOS: DraftSolos = { clips: false, asmr: false, music: false, voice: false, soundtrack: false };
/** The desk's full-length rows: each file runs end to end under the cut and has a lane to set its start. */
export type LaneChannel = 'music' | 'voice' | 'soundtrack';
/** Every desk row that opens a waveform: the full-length three, and the whole-cut views of Timeline and ASMR. */
export type DeskLane = LaneChannel | 'clips' | 'asmr';
/** The full-length rows' words: each holds segments one after another, a `unit` each. */
export const LAYER_ROWS: Record<LaneChannel, { glyph: string; title: string; sub: string; name: string; unit: string; knob: string; ask: string }> = {
  music: { glyph: '♫', title: 'Music', sub: 'Under the whole cut', name: 'the music', unit: 'Song', knob: 'Music volume', ask: 'Describe the music you want' },
  voice: { glyph: '🎙', title: 'Voiceover', sub: 'Over the whole cut', name: 'the voiceover', unit: 'Take', knob: 'Voiceover volume', ask: 'Describe the voiceover you want' },
  soundtrack: { glyph: '♪', title: 'Soundtrack', sub: 'An effects bed', name: 'the soundtrack', unit: 'Sound', knob: 'Soundtrack volume', ask: 'Describe the soundtrack you want' },
};
/** The one queue every direct edit of the open draft waits in, so saves land in the order they were made. */
export const DRAFT_EDIT_QUEUE = 'compose-draft-edits';
/** Direct edits of the draft still saving or waiting to. */
export function draftEditsPending(): number {
  return queryClient.isMutating({ mutationKey: trpc.compose.update.mutationKey() }) + queryClient.isMutating({ mutationKey: trpc.compose.slip.mutationKey() });
}

export function useDraftDesk(opts: {
  draftId: string | null;
  /** A composition another page just made: its sounds are new to it, so it levels as it opens. */
  freshDraftId: string | null;
  /** That composition has levelled, so a reload must not treat its sounds as new again. */
  onFreshSpent?: (draftId: string) => void;
}) {
  const { draftId } = opts;
  const [renderId, setRenderId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // What the last sound action did, in the status line the assembling phrase already uses.
  const [audioNote, setAudioNote] = useState<string | null>(null);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [stagedSlots, setStagedSlots] = useState<Record<number, { inMs: number; outMs: number }>>({});
  const [activeEdge, setActiveEdge] = useState<Record<number, 'in' | 'out'>>({});
  // A sound effect being dragged along its lane, and the per-card sound prompt.
  const [offsetDrafts, setOffsetDrafts] = useState<Record<number, number>>({});
  // A slip being dragged, per slot: the card and the Timeline strip both read it, so each follows the other.
  const [slipDrafts, setSlipDrafts] = useState<Record<number, number>>({});
  // The full-length row whose lane is open under it, and its start while being dragged.
  const [laneOpen, setLaneOpen] = useState<DeskLane | null>(null);
  // Clearing every clip's sound waits on this: how many sounds it would take away.
  const [clearAsk, setClearAsk] = useState<number | null>(null);
  // Replacing every clip's sound waits on this.
  const [replaceAsk, setReplaceAsk] = useState(false);
  // The selected sound on the desk (2026-10-03): one ASMR sound by its slot, or one full-length lane's
  // segment, shaded with its trim handles until another is pressed; and a handle's trim while it is dragged.
  const [selectedSound, setSelectedSound] = useState<number | null>(null);
  const [selectedLayer, setSelectedLayer] = useState<LaneChannel | null>(null);
  const [trimDrafts, setTrimDrafts] = useState<Record<number, { fromMs: number; toMs: number }>>({});
  // Each full-length channel's chosen segment (what its fields, Replace and Level act on), the channel the
  // ＋ is adding a segment to, a segment being dragged or trimmed, and what the last Replace found.
  const [segPick, setSegPick] = useState<Record<LaneChannel, number>>({ music: 0, voice: 0, soundtrack: 0 });
  const [adding, setAdding] = useState<LaneChannel | null>(null);
  const [segDraft, setSegDraft] = useState<{ ch: LaneChannel; index: number; atMs: number; fromMs: number; toMs: number } | null>(null);
  const [layerNote, setLayerNote] = useState<{ ch: LaneChannel; text: string } | null>(null);
  // What the meters heard while the live mix last played.
  const [liveStats, setLiveStats] = useState<Partial<Record<AudioChannel | 'master', ChannelLevel | null>> | null>(null);
  const mixRef = useRef<PreviewMix | null>(null);
  const mix = () => (mixRef.current ??= new PreviewMix());
  useEffect(() => () => mixRef.current?.close(), []);
  // The collapsible desks and the per-channel sparkle popover (anchored where the user clicked).
  const [audioOpen, setAudioOpen] = useState(false);
  const [ask, setAsk] = useState<{ channel: AskChannel; x: number; y: number } | null>(null);
  const [warnOpen, setWarnOpen] = useState(false);
  const [previewSlot, setPreviewSlot] = useState<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  // The client preview's second player, which of the two is in front, the per-frame watch, a
  // hand-over waiting on the next slot's seek, and the swap flag that keeps the play overlay hidden.
  const altVideoRef = useRef<HTMLVideoElement>(null);
  const frontRef = useRef<0 | 1>(0);
  const [frontVideo, setFrontVideo] = useState<0 | 1>(0);
  const watchRef = useRef<{ el: HTMLVideoElement; handle: number } | null>(null);
  const swapTimerRef = useRef<number | null>(null);
  const handOverRef = useRef<{ el: HTMLVideoElement; fn: () => void } | null>(null);
  const swappingRef = useRef(false);
  // The playhead (2026-10-04): where play starts while paused, the slot the chain is playing, and whether a
  // drag of the scrubber interrupted playback that should carry on when it is let go.
  const cursorRef = useRef(0);
  const playingSlotRef = useRef<number | null>(null);
  const resumeAfterSeek = useRef(false);
  useEffect(() => () => {
    if (watchRef.current) watchRef.current.el.cancelVideoFrameCallback(watchRef.current.handle);
    if (swapTimerRef.current !== null) window.clearTimeout(swapTimerRef.current);
  }, []);
  const draft = useQuery(trpc.compose.get.queryOptions({ draftId: draftId ?? '' }, { enabled: draftId !== null }));
  const beds = useQuery(trpc.compose.beds.queryOptions());
  const render = useQuery(trpc.compose.renderStatus.queryOptions({ renderId: renderId ?? '' }, {
    enabled: renderId !== null,
    // An Export polls often enough for its progress bar to move.
    refetchInterval: (q) => (q.state.data?.status === 'done' || q.state.data?.status === 'failed' ? false : EXPORT_POLL_MS),
  }));
  const refresh = async () => {
    await Promise.all([
      draftId ? queryClient.invalidateQueries({ queryKey: trpc.compose.get.queryKey({ draftId }) }) : Promise.resolve(),
      queryClient.invalidateQueries({ queryKey: trpc.compose.list.queryKey() }),
    ]);
  };
  // Slot edits land in the cache optimistically before the mutation, so the timeline never waits on
  // the round trip; the refetch only runs once the last in-flight update settles.
  // Every direct edit of the draft goes through one queue (Dax, 2026-09-30): two quick clicks on a
  // speaker sent two saves at once, the server could apply them in either order, and the stored mute ended
  // up the opposite of the last click. Queued, they land in the order they were made.
  const update = useMutation(trpc.compose.update.mutationOptions({
    scope: { id: DRAFT_EDIT_QUEUE },
    onSettled: async () => {
      if (draftEditsPending() === 1) await refresh();
    },
  }));
  // The toolbar's save indicator: every slot edit is an update, so their progress is the draft's.
  const updating = useIsMutating({ mutationKey: trpc.compose.update.mutationKey() }) + useIsMutating({ mutationKey: trpc.compose.slip.mutationKey() }) > 0;
  // A composition on screen is always saved: it was loaded from the database, and every edit autosaves.
  // So an untouched draft reads Saved rather than leaving a blank cell in the toolbar.
  const draftSave: SaveState = updating ? 'saving' : update.isError ? 'error' : 'saved';
  const slip = useMutation(trpc.compose.slip.mutationOptions({
    scope: { id: DRAFT_EDIT_QUEUE },
    onSettled: async () => {
      if (draftEditsPending() === 1) await refresh();
    },
  }));
  // Sound across the cut, and the next sample for one clip. Both rewrite slots, so both refresh.
  const fillAudio = useMutation(trpc.compose.fillSlotAudio.mutationOptions({
    onSuccess: async (r) => {
      setAudioNote(r.filled
        ? `Sound on ${r.filled} clip${r.filled > 1 ? 's' : ''}${r.unmatched ? `; ${r.unmatched} found nothing` : ''}`
        : r.unmatched ? 'Nothing in the audio library fits these clips yet' : 'Every clip already has a sound');
      await refresh();
    },
    onError: (e) => setAudioNote(e.message),
  }));
  const refreshAudio = useMutation(trpc.compose.refreshSlotAudio.mutationOptions({
    onSuccess: async (r) => {
      setAudioNote(`${r.name} · ${r.at} of ${r.of}`);
      await refresh();
    },
    onError: (e) => setAudioNote(e.message),
  }));
  const audioInstruct = useMutation(trpc.compose.audioInstruct.mutationOptions({ onSuccess: refresh }));
  const replaceLayer = useMutation(trpc.compose.replaceLayer.mutationOptions({
    onSuccess: async (r, vars) => {
      setLayerNote({ ch: vars.channel, text: r.label });
      await refresh();
    },
    onError: (e, vars) => setLayerNote({ ch: vars.channel, text: e.message }),
  }));
  const d = draft.data;
  const totalMs = d?.slots.reduce((acc, s) => acc + (s.outMs - s.inMs), 0) ?? 0;
  /** The ASMR channel is on while any clip carries a sound effect. */
  const asmrOn = Boolean(d?.slots.some((s) => s.audio2));
  /**
   * A full-length channel's segments, each with its file once the file has caught up with it (the file
   * names the sound or clip it is, so a segment just changed in the cache never plays the one it replaced).
   */
  const segsOf = (draft: NonNullable<typeof d>, ch: LaneChannel) => draft.layers[ch].map((seg, i) => {
    const f = draft.layerFiles[ch][i];
    return { seg, file: f && f.id === (seg.bedId ?? seg.assetId) ? f : null };
  });
  /** The segment a channel's fields, Replace and Level act on: the one last chosen, kept in range. */
  const segIndex = (ch: LaneChannel) => Math.max(0, Math.min(segPick[ch], (d?.layers[ch].length ?? 1) - 1));
  // Each clip's colours (clip-hues.ts): the same tint on its card and on the desk's strips.
  const slotCount = d?.slots.length ?? 0;
  const clipTints = useMemo(() => Array.from({ length: slotCount }, (_, i) => clipColours(i, slotCount)), [slotCount]);
  // The live mix: every channel placed on the cut's timeline from the draft as it stands in the cache,
  // so a slider moves the sound while it plays and a new sound is fetched before play is pressed.
  const planOf = (draft: NonNullable<typeof d>, solos: DraftSolos) => planMix({
    slots: draft.slots,
    sounds: draft.slotMeta.map((m, i) => (m?.sound && draft.slots[i]?.audio2 && m.sound.url ? { url: m.sound.url, fromMs: m.sound.fromMs, durationMs: m.sound.durationMs } : null)),
    layers: Object.fromEntries(LAYER_CHANNELS.map((ch) => [ch, segsOf(draft, ch).map(({ seg, file }) => ({
      seg: { atMs: seg.atMs, fromMs: seg.fromMs, toMs: seg.toMs, gainDb: seg.gainDb },
      file: file ? { url: file.url, fromMs: file.fromMs, durationMs: file.durationMs } : null,
    }))])) as MixDraft['layers'],
    clipsGainDb: draft.clipsGainDb, asmrGainDb: draft.asmrGainDb, bedGainDb: draft.bedGainDb, musicGainDb: draft.musicGainDb, voiceGainDb: draft.voiceGainDb, masterGainDb: draft.masterGainDb,
    mutes: draft.mutes, solos, audioFx: draft.audioFx,
  });
  const mixPlan = useMemo(() => (d ? planOf(d, d.solos) : null), [d]);
  useEffect(() => {
    if (!mixPlan) return;
    mixRef.current?.apply(mixPlan);
    mix().preload(mixPlan);
  }, [mixPlan]);
  useEffect(() => {
    if (!previewPlaying) {
      if (mixRef.current) setLiveStats(mixRef.current.levels());
      return;
    }
    const t = setInterval(() => { if (mixRef.current) setLiveStats(mixRef.current.levels()); }, METER_POLL_MS);
    return () => clearInterval(t);
  }, [previewPlaying]);
  /** A channel's meter: what the live mix last heard, else the last Export's measurement. */
  const exportStats = render.data?.audioStats ?? null;
  const levelOf = (ch: AudioChannel | 'master'): ChannelLevel | null => (liveStats ? liveStats[ch] ?? null : exportStats?.[ch] ?? null);
  /**
   * The master's level (2026-10-04): its own gain after the EQ. With its Level toggle on (the default) it follows
   * the whole mix (`levelMaster`, below) and a pass that clips pulls it back (masterCorrection); a knob turned by
   * hand switches the toggle off and holds, and the warning then says if the mix clips. A reading taken before
   * the master last moved is stale until the next pass, so it warns of nothing.
   */
  const [masterStale, setMasterStale] = useState(false);
  const masterAuto = d?.autoLevel.master ?? true;
  const setMaster = (masterGainDb: number) => {
    if (!d) return;
    patchDraft({ masterGainDb });
    update.mutate({ draftId: d.id, masterGainDb });
    setMasterStale(true);
  };
  const masterWarn = (() => {
    const m = levelOf('master');
    const next = m && d && !masterStale ? masterCorrection(m, d.masterGainDb) : null;
    if (!m || !d || next === null) return null;
    return { text: `Clipping: peaks reach ${m.peakDb.toFixed(1)} dB. Fix now lowers the master ${Math.abs(next - d.masterGainDb)} dB to bring peaks under ${MASTER_PEAK_CEILING_DB} dB.`, next };
  })();
  const fixMaster = () => {
    if (masterWarn) setMaster(masterWarn.next);
    setWarnOpen(false);
  };
  // After each pass, the master's toggle pulls back a mix that clipped. A solo is someone listening, not the mix.
  const passHeard = useRef(false);
  useEffect(() => {
    if (previewPlaying) {
      passHeard.current = true;
      setMasterStale(false);
      return;
    }
    if (!passHeard.current || !d || !mixRef.current) return;
    passHeard.current = false;
    const m = mixRef.current.levels().master;
    const next = m && d.autoLevel.master && !masterStale && !Object.values(d.solos).some(Boolean) ? masterCorrection(m, d.masterGainDb) : null;
    if (next !== null) setMaster(next);
  }, [previewPlaying]);
  /** Wake the live mix from the click and route both players through it (once each, for their life). */
  const wakeMix = () => {
    const m = mix();
    m.ensure();
    if (videoRef.current) m.attach(videoRef.current);
    if (altVideoRef.current) m.attach(altVideoRef.current);
    return m;
  };
  const playPreview = () => {
    const v = videoRef.current;
    if (!v || !d || !mixPlan) return;
    if (!previewUrl) {
      startPreviewAt(cursorRef.current >= totalMs - PLAYHEAD_END_RESTART_MS ? 0 : cursorRef.current);
      return;
    }
    haltPreview();
    setPreviewSlot(null);
    showFront(0);
    const m = wakeMix();
    // A paused picture resumes where it stopped, and the mix with it.
    const fromMs = v.src === mixUrl(previewUrl) && !v.ended ? Math.round(v.currentTime * 1000) : 0;
    if (v.src !== mixUrl(previewUrl)) v.src = mixUrl(previewUrl);
    // One file carries every slot's own sound at its unchanged level; the mix steps each slot's level.
    m.scheduleElementGains(v, mixPlan, fromMs);
    void v.play();
    void m.start(mixPlan, fromMs, mixPlan.totalMs);
  };
  /** Instant slider feedback: the cache takes the value on drag, the mutation lands on release. */
  const patchDraft = (patch: Record<string, unknown>) => {
    if (!d) return;
    // A refetch already on its way carries the draft from before this edit; it must not land over it.
    void queryClient.cancelQueries({ queryKey: trpc.compose.get.queryKey({ draftId: d.id }) });
    queryClient.setQueryData(trpc.compose.get.queryKey({ draftId: d.id }), (old: typeof d | undefined) => (old ? { ...old, ...patch } : old));
  };
  /** A track's solo: soloed or not at once, saved like every edit (Dax, 2026-09-30). */
  const setSolos = (patch: Partial<DraftSolos>) => {
    if (!d) return;
    const solos = { ...d.solos, ...patch };
    patchDraft({ solos });
    update.mutate({ draftId: d.id, solos });
  };
  /** Not muted itself but silenced because another channel is soloed. */
  const silenced = (ch: AudioChannel) => Boolean(d && !d.mutes[ch] && !channelHeard(ch, d.mutes, d.solos));
  /** Why a card's knob is greyed: its channel muted, or silenced by a solo, in the audio panel. */
  const panelNote = (ch: AudioChannel) => (!d ? null : d.mutes[ch] ? 'Muted in the audio panel' : silenced(ch) ? 'Silenced by a solo in the audio panel' : null);
  /** A track's speaker: heard or not at once, saved like every edit. */
  const setMutes = (patch: Partial<DraftMutes>) => {
    if (!d) return;
    const mutes = { ...d.mutes, ...patch };
    patchDraft({ mutes });
    update.mutate({ draftId: d.id, mutes });
  };
  /** Whether a full-length channel holds anything, and whether any of it has a file to draw yet. */
  const hasLayer = (ch: LaneChannel) => Boolean(d?.layers[ch].length);
  const layerDrawn = (ch: LaneChannel) => Boolean(d && segsOf(d, ch).some((g) => g.file));
  /** Whether a row has a waveform to show. A full-length row is coloured whenever it holds a file (Dax, 2026-09-29). */
  const laneReady = (ch: DeskLane) => (ch === 'clips' ? Boolean(d?.slots.length) : ch === 'asmr' ? asmrOn : layerDrawn(ch));
  const laneShown = (ch: DeskLane) => laneOpen === ch && laneReady(ch);
  /**
   * A row's waveform opens and closes from its chevron and nothing else (Dax, 2026-10-31): the desk only moves
   * when someone asks to see a waveform, never for a click on the row or on one of its controls. Dragging a
   * file in its lane sets where it starts against the cut's first frame.
   */
  const laneChevron = (ch: DeskLane) => (
    <button type="button" className="lane-toggle" aria-expanded={laneShown(ch)} disabled={!laneReady(ch)} aria-label="Waveform"
      title={laneShown(ch) ? 'Hide the waveform' : ch === 'clips' || ch === 'asmr' ? 'Show the whole cut' : 'Show the waveform, to drag where it starts'}
      onClick={() => setLaneOpen(laneShown(ch) ? null : ch)}>
      <Chevron up={laneShown(ch)} />
    </button>
  );
  /**
   * The whole cut as one strip for Timeline and ASMR (Dax, 2026-09-29): the clips' own sound end to end
   * with a mark at each cut, or every clip's effect where it sits. A view; the cards do the editing.
   */
  const stripFor = (ch: 'clips' | 'asmr') => {
    if (!d || !mixPlan || !laneShown(ch)) return null;
    const cuts = mixPlan.slots.slice(1).map((s) => s.startMs);
    const segments = d.slots.flatMap((slot, i) => {
      const meta = d.slotMeta[i];
      const at = mixPlan.slots[i]!.startMs;
      if (ch === 'clips') {
        if (!meta?.peaks.length) return [];
        // A slip in progress, on the card or here, shows the footage it is moving to.
        const shift = slipDrafts[i] ?? 0;
        const i0 = Math.floor(((slot.inMs + shift) / 1000) * PEAKS_PER_SECOND);
        const i1 = Math.ceil(((slot.outMs + shift) / 1000) * PEAKS_PER_SECOND);
        const len = slot.outMs - slot.inMs;
        return [{ key: i, atMs: at, durationMs: len, peaks: meta.peaks.slice(i0, i1), heardFromMs: 0, heardToMs: len, off: slot.audio === 'mute' || d.mutes.clips, holdFromMs: at, holdToMs: at + len, colours: clipTints[i] }];
      }
      if (!slot.audio2 || !meta?.sound) return [];
      // The whole sample where it sits, of which its trim (or the trim being dragged) plays.
      const trim = trimDrafts[i] ?? { fromMs: slot.audio2.trimFromMs, toMs: slot.audio2.trimToMs ?? meta.sound.durationMs };
      return [{ key: i, atMs: at + (offsetDrafts[i] ?? slot.audio2.offsetMs), durationMs: meta.sound.durationMs, peaks: meta.sound.peaks, heardFromMs: trim.fromMs, heardToMs: trim.toMs, off: slot.audio2.muted || d.mutes.asmr, holdFromMs: at, holdToMs: at + slot.outMs - slot.inMs, colours: clipTints[i] }];
    });
    return (
      <div className="audio-lane">
        {/* On the strip the sound moves under a fixed window, so dragging right brings earlier footage in. */}
        <CutStrip segments={segments} totalMs={totalMs} cuts={cuts} label={ch === 'clips' ? 'Every clip\'s own sound across the cut' : 'Every clip\'s sound effect across the cut'}
          onDrag={(key, deltaMs, done) => (ch === 'clips' ? dragSlip(key, -deltaMs, done) : dragSound(key, deltaMs, done))}
          {...(ch === 'asmr' ? { selected: selectedSound, onSelect: setSelectedSound, onTrim: trimSound } : {})} />
      </div>
    );
  };
  /** A channel's segments written back: at once in the cache, then saved. */
  const writeLayer = (ch: LaneChannel, segs: LayerSegment[]) => {
    if (!d) return;
    patchDraft({ layers: { ...d.layers, [ch]: segs } });
    update.mutate({ draftId: d.id, layers: { [ch]: segs } });
  };
  /**
   * A segment dragged along its lane, or one of its handles. A drag moves where its file starts, always
   * leaving some of what plays inside the cut so it can be grabbed again; a trim never moves the sound,
   * stays inside its file and is never shorter than TRIM_MIN_MS.
   */
  const dragSegment = (ch: LaneChannel, index: number, deltaMs: number, done: boolean) => {
    const g = d ? segsOf(d, ch)[index] : undefined;
    if (!d || !g?.file) return;
    const { fromMs, toMs } = segmentStretch(g.seg, g.file.durationMs);
    const lo = SFX_NUDGE_MIN_OVERLAP_MS - toMs;
    const hi = totalMs - SFX_NUDGE_MIN_OVERLAP_MS - fromMs;
    const atMs = Math.round(Math.max(lo, Math.min(hi, g.seg.atMs + deltaMs)));
    if (!done) {
      setSegDraft({ ch, index, atMs, fromMs, toMs });
      return;
    }
    setSegDraft(null);
    if (atMs !== g.seg.atMs) writeLayer(ch, d.layers[ch].map((x, i) => (i === index ? { ...x, atMs } : x)));
  };
  const trimSegment = (ch: LaneChannel, index: number, edge: TrimEdge, deltaMs: number, done: boolean) => {
    const g = d ? segsOf(d, ch)[index] : undefined;
    if (!d || !g?.file) return;
    const fileMs = g.file.durationMs;
    const { fromMs: from, toMs: to } = segmentStretch(g.seg, fileMs);
    const next = edge === 'start'
      ? { fromMs: Math.round(Math.max(0, Math.min(to - TRIM_MIN_MS, from + deltaMs))), toMs: to }
      : { fromMs: from, toMs: Math.round(Math.min(fileMs, Math.max(from + TRIM_MIN_MS, to + deltaMs))) };
    if (!done) {
      setSegDraft({ ch, index, atMs: g.seg.atMs, ...next });
      return;
    }
    setSegDraft(null);
    if (next.fromMs === from && next.toMs === to) return;
    writeLayer(ch, d.layers[ch].map((x, i) => (i === index ? { ...x, fromMs: next.fromMs, toMs: next.toMs >= fileMs ? null : next.toMs } : x)));
  };
  const laneFor = (ch: LaneChannel) => {
    if (!d || !laneShown(ch)) return null;
    const pieces = segsOf(d, ch).map(({ seg, file }, i) => {
      if (!file) return null;
      const live = segDraft?.ch === ch && segDraft.index === i ? segDraft : null;
      const stretch = live ?? { atMs: seg.atMs, ...segmentStretch(seg, file.durationMs) };
      return { file, atMs: stretch.atMs, fromMs: stretch.fromMs, toMs: stretch.toMs };
    });
    const name = ch === 'music' ? 'music' : ch === 'voice' ? 'voiceover' : 'soundtrack';
    return (
      <div className="audio-lane">
        <LayerLane mix={mix()} label={`The ${name} across the cut`} pieces={pieces} windowMs={totalMs} muted={d.mutes[ch]}
          selected={selectedLayer === ch ? segIndex(ch) : null}
          onSelect={(i) => {
            setSelectedLayer(ch);
            setSegPick((p) => ({ ...p, [ch]: i }));
            setAdding(null);
          }}
          onDrag={(i, delta, done) => dragSegment(ch, i, delta, done)}
          onTrim={(i, edge, delta, done) => trimSegment(ch, i, edge, delta, done)} />
      </div>
    );
  };
  /**
   * A handle on the ASMR strip: the selected sound's trim, moved by `deltaMs` from where it was, inside
   * its sample and never shorter than TRIM_MIN_MS. An end dragged by hand drops the fade Trim clip set.
   */
  const trimSound = (index: number, edge: TrimEdge, deltaMs: number, done: boolean) => {
    const slot = d?.slots[index];
    const fileMs = d?.slotMeta[index]?.sound?.durationMs;
    if (!d || !slot?.audio2 || !fileMs) return;
    const from = slot.audio2.trimFromMs;
    const to = slot.audio2.trimToMs ?? fileMs;
    const next = edge === 'start'
      ? { fromMs: Math.round(Math.max(0, Math.min(to - TRIM_MIN_MS, from + deltaMs))), toMs: to }
      : { fromMs: from, toMs: Math.round(Math.min(fileMs, Math.max(from + TRIM_MIN_MS, to + deltaMs))) };
    if (!done) {
      setTrimDrafts((t) => ({ ...t, [index]: next }));
      return;
    }
    setTrimDrafts((t) => {
      const { [index]: gone, ...rest } = t;
      void gone;
      return rest;
    });
    if (next.fromMs === from && next.toMs === to) return;
    const audio2 = { ...slot.audio2, trimFromMs: next.fromMs, trimToMs: next.toMs >= fileMs ? null : next.toMs, endFadeMs: edge === 'end' ? null : slot.audio2.endFadeMs };
    // Dragged past its clip's edge, a sound leaves Trim clip: the toggle goes off, the other sounds stay as they are.
    const clipStart = d.slots.slice(0, index).reduce((acc, s) => acc + (s.outMs - s.inMs), 0);
    if (d.asmrTrim && trimLeavesClip({ fromMs: audio2.trimFromMs, toMs: audio2.trimToMs }, clipStart, clipStart + slot.outMs - slot.inMs, clipStart + slot.audio2.offsetMs, fileMs)) {
      const slots = d.slots.map((s, i) => (i === index ? { ...s, audio2 } : s));
      patchDraft({ asmrTrim: false });
      applySlots(slots);
      update.mutate({ draftId: d.id, slots, asmrTrim: false });
      return;
    }
    setSlot(index, { audio2 });
  };
  /**
   * Trim clip, a toggle (2026-10-04): while on, every clip's sound is held inside its own clip
   * (`clampToClip`), a sound brought in, swapped or pushed included; a handle dragged further in stays.
   * Turning it off plays every sound whole again. Returns the slots as they should be, unchanged when
   * nothing needs holding (a sound whose file has not caught up is left for the next pass).
   */
  const clampedSlots = (draft: NonNullable<typeof d>) => {
    let at = 0;
    return draft.slots.map((s, i) => {
      const start = at;
      at += s.outMs - s.inMs;
      const sound = draft.slotMeta[i]?.sound;
      if (!s.audio2 || !sound || sound.id !== s.audio2.bedId) return s;
      const t = clampToClip({ fromMs: s.audio2.trimFromMs, toMs: s.audio2.trimToMs, endFadeMs: s.audio2.endFadeMs }, start, at, start + s.audio2.offsetMs, sound.durationMs);
      if (!t || (t.fromMs === s.audio2.trimFromMs && t.toMs === s.audio2.trimToMs && t.endFadeMs === s.audio2.endFadeMs)) return s;
      return { ...s, audio2: { ...s.audio2, trimFromMs: t.fromMs, trimToMs: t.toMs, endFadeMs: t.endFadeMs } };
    });
  };
  const toggleTrim = () => {
    if (!d) return;
    const asmrTrim = !d.asmrTrim;
    const slots = asmrTrim ? clampedSlots(d) : d.slots.map((s) => (s.audio2 ? { ...s, audio2: { ...s.audio2, trimFromMs: 0, trimToMs: null, endFadeMs: null } } : s));
    patchDraft({ asmrTrim });
    applySlots(slots);
    update.mutate({ draftId: d.id, slots, asmrTrim });
  };
  useEffect(() => {
    if (!d?.asmrTrim) return;
    const slots = clampedSlots(d);
    if (slots.every((s, i) => s === d.slots[i])) return;
    applySlots(slots);
    update.mutate({ draftId: d.id, slots });
  }, [d]);
  /**
   * A desk row's level and its mute on one knob, as on the cards (Dax, 2026-09-30): turning moves the sound
   * live, the release saves; turning all the way down is the row's speaker.
   */
  /**
   * A level set by hand holds (2026-10-04): turning any of a channel's knobs switches its Level off, as the
   * master's does, and switching Level on again brings every level back to the system's.
   */
  const levelOffByHand = (ch: AudioChannel) => (d?.autoLevel[ch] ? { autoLevel: { ...d.autoLevel, [ch]: false } } : {});
  const offByHand = (ch: AudioChannel) => {
    const patch = levelOffByHand(ch);
    if (!d || !patch.autoLevel) return;
    patchDraft(patch);
    update.mutate({ draftId: d.id, ...patch });
  };
  const channelKnob = (ch: AudioChannel, label: string, db: number, gain: (db: number) => Record<string, unknown>, disabled: boolean) => {
    if (!d) return null;
    return (
      <Knob label={label} min={GAIN_MIN} max={GAIN_MAX} value={{ db, muted: d.mutes[ch] }} disabled={disabled}
        dimmed={silenced(ch) ? 'Silenced by a solo' : null}
        onChange={(v, done) => {
          const patch = { ...gain(v.db), mutes: { ...d.mutes, [ch]: v.muted }, ...(v.db !== db ? levelOffByHand(ch) : {}) };
          patchDraft(patch);
          if (done) update.mutate({ draftId: d.id, ...patch });
        }} />
    );
  };
  /**
   * Levelling (Dax, 2026-10-03): each sound measured over what plays, from the audio the live mix decodes,
   * and given the gain that brings it to its channel's target (`levelGainDb`). Timeline and ASMR set each
   * clip's own knob; music, voiceover and soundtrack set the channel's. Results apply to the draft as it
   * stands when they land, matched by the clip and the sound they were measured for, so an edit made
   * meanwhile is never overwritten; a sound changed meanwhile is simply not touched.
   */
  const [levelling, setLevelling] = useState<Partial<Record<AudioChannel, boolean>>>({});
  const latestDraft = () => (d ? queryClient.getQueryData(trpc.compose.get.queryKey({ draftId: d.id })) as typeof d | undefined : undefined);
  const gainRange = { min: GAIN_MIN, max: GAIN_MAX };
  /** What of slot i's sound plays, as a stretch of its file: its trim, inside the cut (`placeSound`). */
  const soundStretch = (draft: NonNullable<typeof d>, i: number): { url: string; fromMs: number; durationMs: number } | null => {
    const slot = draft.slots[i];
    const sound = draft.slotMeta[i]?.sound;
    if (!slot?.audio2 || !sound) return null;
    const start = draft.slots.slice(0, i).reduce((acc, s) => acc + (s.outMs - s.inMs), 0);
    const total = draft.slots.reduce((acc, s) => acc + (s.outMs - s.inMs), 0);
    const p = placeSound({ atMs: start + slot.audio2.offsetMs, fileMs: sound.durationMs, fromMs: slot.audio2.trimFromMs, toMs: slot.audio2.trimToMs, endFadeMs: slot.audio2.endFadeMs }, total);
    return p ? { url: sound.url, fromMs: sound.fromMs + p.fromMs, durationMs: p.durationMs } : null;
  };
  /** Each clip's sound effect to the ASMR target; `only` limits it to the clips whose sound just changed. */
  const levelSounds = async (only?: Set<string>) => {
    const base = latestDraft();
    if (!base) return;
    const gains = new Map<string, number>();
    await Promise.all(base.slots.map(async (s, i) => {
      if (!s.audio2 || (only && !only.has(s.snippetId))) return;
      const stretch = soundStretch(base, i);
      const m = stretch ? await mix().measure(stretch.url, stretch.fromMs, stretch.durationMs) : null;
      const g = m ? levelGainDb(m, LEVEL_TARGET_DB.asmr, gainRange) : null;
      if (g !== null) gains.set(`${s.snippetId}|${s.audio2.bedId}`, g);
    }));
    const now = latestDraft();
    if (!now || !gains.size) return;
    const slots = now.slots.map((s) => {
      const g = s.audio2 ? gains.get(`${s.snippetId}|${s.audio2.bedId}`) : undefined;
      return g === undefined || !s.audio2 ? s : { ...s, audio2: { ...s.audio2, gainDb: g } };
    });
    // Level owns the channel while it is on, its own knob at 0 dB, so what was measured is what plays.
    const fader = { asmrGainDb: 0 };
    applySlots(slots);
    patchDraft(fader);
    update.mutate({ draftId: now.id, slots, ...fader });
  };
  /** Each clip's own sound, read from its proxy, to the timeline target; `only` limits it to clips just brought in. */
  const levelClips = async (only?: Set<string>) => {
    const base = latestDraft();
    if (!base) return;
    const gains = new Map<string, number>();
    await Promise.all(base.slots.map(async (s, i) => {
      const url = base.slotMeta[i]?.proxyUrl;
      if (s.audio === 'mute' || !url || (only && !only.has(s.snippetId))) return;
      if ((base.slotMeta[i]?.assetMs ?? 0) > LEVEL_DECODE_MAX_MS) return;
      const m = await mix().measure(url, s.inMs, s.outMs - s.inMs);
      const g = m ? levelGainDb(m, LEVEL_TARGET_DB.clips, gainRange) : null;
      if (g !== null) gains.set(`${s.snippetId}|${s.inMs}|${s.outMs}`, g);
    }));
    const now = latestDraft();
    if (!now || !gains.size) return;
    const slots = now.slots.map((s) => {
      const g = gains.get(`${s.snippetId}|${s.inMs}|${s.outMs}`);
      return g === undefined ? s : { ...s, gainDb: g };
    });
    const fader = { clipsGainDb: 0 };
    applySlots(slots);
    patchDraft(fader);
    update.mutate({ draftId: now.id, slots, ...fader });
  };
  /**
   * Each segment of a full-length channel, over what of it plays inside the cut, to the channel's target
   * on its own level; `only` limits it to the segments just brought in. The fader returns to 0 dB either way,
   * so what was measured is what plays: a song levelled under the desk's -12 dB default sat 12 dB too low.
   */
  const levelLayer = async (ch: LaneChannel, only?: Set<number>) => {
    const base = latestDraft();
    if (!base) return;
    const total = base.slots.reduce((acc, s) => acc + (s.outMs - s.inMs), 0);
    const gains = new Map<number, { source: string; db: number }>();
    await Promise.all(segsOf(base, ch).map(async ({ seg, file }, i) => {
      if (!file || (only && !only.has(i))) return;
      const { fromMs, toMs } = segmentStretch(seg, file.durationMs);
      const begins = Math.max(0, seg.atMs + fromMs);
      const ends = Math.min(total, seg.atMs + toMs);
      if (ends <= begins) return;
      const m = await mix().measure(file.url, file.fromMs + begins - seg.atMs, ends - begins);
      const g = m ? levelGainDb(m, LEVEL_TARGET_DB[ch], gainRange) : null;
      if (g !== null) gains.set(i, { source: file.id, db: g });
    }));
    const now = latestDraft();
    if (!now || !gains.size) return;
    // Only a segment still holding the sound it was measured for takes the reading.
    const segs = now.layers[ch].map((g, i) => {
      const r = gains.get(i);
      return r && r.source === (g.bedId ?? g.assetId) ? { ...g, gainDb: r.db } : g;
    });
    const fader = ch === 'music' ? { musicGainDb: 0 } : ch === 'voice' ? { voiceGainDb: 0 } : { bedGainDb: 0 };
    patchDraft({ layers: { ...now.layers, [ch]: segs }, ...fader });
    update.mutate({ draftId: now.id, layers: { [ch]: segs }, ...fader });
  };
  const levelChannel = async (ch: AudioChannel) => {
    setLevelling((l) => ({ ...l, [ch]: true }));
    try {
      if (ch === 'asmr') await levelSounds();
      else if (ch === 'clips') await levelClips();
      else await levelLayer(ch);
    } finally {
      setLevelling((l) => ({ ...l, [ch]: false }));
    }
  };
  /**
   * Level, a toggle per channel (2026-10-04), on by default: while on, a sound or clip arriving on the
   * channel is levelled automatically; a knob turned by hand leaves it on. Switching it on levels the
   * channel there and then.
   */
  const levelTool = (ch: AudioChannel, label: string, disabled: boolean) => {
    const on = d?.autoLevel[ch] ?? true;
    return (
      <Tool icon="level" toggle="level" pressed={on} disabled={disabled || Boolean(levelling[ch])}
        title={on ? `Level ${label}: on, every sound arriving here is levelled. Click to stop` : `Level ${label}: off. Click to level it now and as sounds arrive`}
        onClick={() => {
          if (!d) return;
          const autoLevel = { ...d.autoLevel, [ch]: !on };
          patchDraft({ autoLevel });
          update.mutate({ draftId: d.id, autoLevel });
          if (!on) void levelChannel(ch);
        }} />
    );
  };
  // A sound dragged in or changed is levelled as it arrives, by whichever route it came (the ask, refresh,
  // the library, a picker): its knob moves from where it was to the measured level. Opening a draft levels
  // nothing, and a level turned by hand stays until the sound changes or Level is pressed. A change is
  // levelled once its file has caught up (the layer's `id` matches), never against the file it replaced.
  const levelSeen = useRef<{ draftId: string | null; keys: Map<string, string> }>({ draftId: null, keys: new Map() });
  // A composition made elsewhere and opened here (the Analyser's Remix) arrives fresh, like the Composer's own Remix.
  const levelFresh = useRef<string | null>(opts.freshDraftId);
  /**
   * A song arriving on the music row skips its silent opening (2026-10-04): an untrimmed segment starts just
   * before its sound begins, the file slid back by as much, so its heard stretch starts where it did.
   */
  /**
   * A clip's sound effect arriving skips its silent opening too (2026-10-04): one still where it was put (no
   * push, no trim) starts at the clip's first frame, SOUND_LEAD_PREROLL_MS before its sound.
   */
  const skipSoundSilence = async (only: Set<string>) => {
    const base = latestDraft();
    if (!base) return;
    const leads = new Map<string, number>();
    await Promise.all(base.slots.map(async (s, i) => {
      const sound = base.slotMeta[i]?.sound;
      if (!s.audio2 || !only.has(s.snippetId) || !sound || sound.id !== s.audio2.bedId || s.audio2.trimFromMs !== 0 || s.audio2.offsetMs !== 0) return;
      const at = await mix().firstSignal(sound.url, MUSIC_SIGNAL_FLOOR_DB);
      if (at !== null && at >= MUSIC_LEAD_SILENCE_MIN_MS) leads.set(`${s.snippetId}|${s.audio2.bedId}`, Math.min(at - SOUND_LEAD_PREROLL_MS, SFX_NUDGE_MAX_MS));
    }));
    const now = latestDraft();
    if (!now || !leads.size) return;
    const slots = now.slots.map((s) => {
      const lead = s.audio2 ? leads.get(`${s.snippetId}|${s.audio2.bedId}`) : undefined;
      if (lead === undefined || !s.audio2 || s.audio2.trimFromMs !== 0 || s.audio2.offsetMs !== 0) return s;
      // The file slides back by its silence, so what plays starts on the clip's first frame.
      return { ...s, audio2: { ...s.audio2, trimFromMs: lead, offsetMs: -lead } };
    });
    applySlots(slots);
    update.mutate({ draftId: now.id, slots });
  };
  const skipLeadSilence = async (only: Set<number>) => {
    const base = latestDraft();
    if (!base) return;
    const leads = new Map<number, { source: string; ms: number }>();
    await Promise.all(segsOf(base, 'music').map(async ({ seg, file }, i) => {
      if (!file || !only.has(i) || seg.fromMs !== 0) return;
      const at = await mix().firstSignal(file.url, MUSIC_SIGNAL_FLOOR_DB);
      if (at !== null && at >= MUSIC_LEAD_SILENCE_MIN_MS) leads.set(i, { source: file.id, ms: at - MUSIC_LEAD_PREROLL_MS });
    }));
    const now = latestDraft();
    if (!now || !leads.size) return;
    const segs = now.layers.music.map((g, i) => {
      const lead = leads.get(i);
      return lead && lead.source === (g.bedId ?? g.assetId) && g.fromMs === 0 ? { ...g, fromMs: lead.ms, atMs: g.atMs - lead.ms } : g;
    });
    patchDraft({ layers: { ...now.layers, music: segs } });
    update.mutate({ draftId: now.id, layers: { music: segs } });
  };
  /**
   * The master follows the mix (Dax, 2026-10-31): whenever what plays changes (a lane arriving, leaving or muted, a
   * level, a trim), the whole cut is mixed down out of earshot and the master set from that, so it moves as a lane
   * comes in and not after someone has pressed play. Everything already there goes down together to make room, the
   * balance between lanes staying as Level set it. Opening a draft levels nothing, as with the sounds in it.
   */
  const masterStems = (draft: NonNullable<typeof d>) => {
    const plan = planOf(draft, NO_SOLOS);
    return { totalMs: plan.totalMs, stems: mixStems(plan, draft.slots.map((s, i) => {
      const url = draft.slotMeta[i]?.proxyUrl;
      return url ? { url, inMs: s.inMs } : null;
    })) };
  };
  const mixKey = (draft: NonNullable<typeof d>) => JSON.stringify([masterStems(draft).stems, draft.audioFx]);
  const levelMaster = async () => {
    const base = latestDraft();
    if (!base?.autoLevel.master || base.slotMeta.some((m) => (m?.assetMs ?? 0) > LEVEL_DECODE_MAX_MS)) return;
    const { totalMs: length, stems } = masterStems(base);
    const key = mixKey(base);
    const m = await mix().measureMix(stems, length, base.audioFx);
    const now = latestDraft();
    // A mix that moved while it was measured has already asked to be read again.
    if (!m || !now?.autoLevel.master || mixKey(now) !== key) return;
    const next = masterLevelDb(m, now.masterGainDb);
    if (next !== null) setMaster(next);
  };
  const masterSeen = useRef<{ draftId: string | null; key: string }>({ draftId: null, key: '' });
  const masterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (masterTimer.current) clearTimeout(masterTimer.current);
  }, []);
  useEffect(() => {
    if (!d) return;
    const key = mixKey(d);
    const moved = masterSeen.current.draftId === d.id ? masterSeen.current.key !== key : levelFresh.current === d.id;
    masterSeen.current = { draftId: d.id, key };
    if (!moved || !d.autoLevel.master) return;
    if (masterTimer.current) clearTimeout(masterTimer.current);
    masterTimer.current = setTimeout(() => void levelMaster(), MASTER_LEVEL_SETTLE_MS);
  }, [d]);
  useEffect(() => {
    if (!d) return;
    const seen = levelSeen.current;
    const opening = seen.draftId !== d.id;
    if (opening) seen.draftId = d.id;
    if (opening) seen.keys = new Map();
    const current = new Map<string, { identity: string; ready: boolean }>();
    d.slots.forEach((s, i) => {
      if (s.audio2) current.set(`asmr:${s.snippetId}`, { identity: s.audio2.bedId, ready: d.slotMeta[i]?.sound?.id === s.audio2.bedId });
      // A clip brought in or swapped: its own sound is levelled as the sounds are (Level on Timeline volume).
      current.set(`clip:${s.snippetId}`, { identity: s.snippetId, ready: Boolean(d.slotMeta[i]?.proxyUrl) });
    });
    // Each segment by its place on the channel and the file alone: trimming or moving it is not a new sound.
    for (const ch of LAYER_CHANNELS) segsOf(d, ch).forEach(({ seg, file }, i) => {
      current.set(`${ch}:${i}`, { identity: seg.bedId ?? seg.assetId ?? '', ready: file !== null });
    });
    const sounds = new Set<string>();
    const clips = new Set<string>();
    const layers = new Map<LaneChannel, Set<number>>();
    // A composition the Remix just made is levelled as it opens, every sound in it new.
    const fresh = levelFresh.current === d.id;
    for (const [key, item] of current) {
      if (seen.keys.get(key) === item.identity || !item.ready) continue;
      seen.keys.set(key, item.identity);
      if (opening && !fresh) continue;
      if (key.startsWith('asmr:')) sounds.add(key.slice('asmr:'.length));
      else if (key.startsWith('clip:')) clips.add(key.slice('clip:'.length));
      else {
        const [ch, i] = key.split(':') as [LaneChannel, string];
        layers.set(ch, (layers.get(ch) ?? new Set()).add(Number(i)));
      }
    }
    for (const key of [...seen.keys.keys()]) if (!current.has(key)) seen.keys.delete(key);
    if (opening && fresh) {
      levelFresh.current = null;
      // Spent: a reload must not treat the composition's sounds as new again.
      opts.onFreshSpent?.(d.id);
    }
    if (sounds.size) void skipSoundSilence(sounds).then(() => (latestDraft()?.autoLevel.asmr ? levelSounds(sounds) : undefined));
    if (clips.size && d.autoLevel.clips) void levelClips(clips);
    for (const [ch, only] of layers) {
      if (ch === 'music') void skipLeadSilence(only).then(() => (latestDraft()?.autoLevel.music ? levelLayer(ch, only) : undefined));
      else if (d.autoLevel[ch]) void levelLayer(ch, only);
    }
  }, [d]);
  /**
   * A row's clear: empties the channel and leaves the row, so nothing has to be brought back. A channel of
   * several pieces (every clip's sound) asks first.
   */
  const clearChannel = (ch: 'asmr' | 'music' | 'voice' | 'soundtrack', confirmed = false) => {
    if (!d) return;
    if (ch === 'asmr') {
      const count = d.slots.filter((s) => s.audio2).length;
      if (count > 1 && !confirmed) {
        setClearAsk(count);
        return;
      }
      const slots = d.slots.map((s) => ({ ...s, audio2: null }));
      applySlots(slots);
      update.mutate({ draftId: d.id, slots });
      return;
    }
    writeLayer(ch, []);
    setSegPick((p) => ({ ...p, [ch]: 0 }));
    if (adding === ch) setAdding(null);
  };
  /**
   * A pick in a full-length row's fields (2026-10-04). In add mode it lays a new segment where the last
   * one's heard stretch ends; otherwise it replaces the chosen segment, starting where that one's stretch
   * started, untrimmed. Choosing a field's label takes the chosen segment away.
   */
  const pickLayer = (ch: LaneChannel, source: { bedId: string } | { assetId: string } | null) => {
    if (!d) return;
    setLayerNote(null);
    const segs = d.layers[ch];
    const fresh = (over: Partial<LayerSegment>): LayerSegment => ({ bedId: null, assetId: null, fromMs: 0, toMs: null, atMs: 0, gainDb: 0, query: null, cursor: 0, ...source, ...over });
    if (adding === ch || !segs.length) {
      setAdding(null);
      if (!source) return;
      const files = segsOf(d, ch);
      const placed = segs.map((g, i) => ({ atMs: g.atMs, fromMs: g.fromMs, toMs: g.toMs, fileMs: files[i]?.file?.durationMs ?? 0 }));
      const atMs = nextSegmentAtMs(placed);
      // A last segment running past the cut's end leaves no room after it: the new one takes the second
      // half of what the last one plays on the cut, and the last is trimmed to end where it starts.
      const last = placed.at(-1);
      const heardFrom = last ? Math.max(0, last.atMs + segmentStretch(last, last.fileMs).fromMs) : 0;
      if (last && atMs >= totalMs && totalMs - heardFrom >= 2 * TRIM_MIN_MS) {
        const split = Math.round(heardFrom + (totalMs - heardFrom) / 2);
        const trimmed = segs.map((g, i) => (i === segs.length - 1 ? { ...g, toMs: split - g.atMs } : g));
        writeLayer(ch, [...trimmed, fresh({ atMs: split })]);
      } else writeLayer(ch, [...segs, fresh({ atMs })]);
      setSegPick((p) => ({ ...p, [ch]: segs.length }));
      return;
    }
    const index = segIndex(ch);
    const old = segs[index]!;
    if (!source) {
      writeLayer(ch, segs.filter((_, i) => i !== index));
      setSegPick((p) => ({ ...p, [ch]: Math.max(0, index - 1) }));
      return;
    }
    // With a segment after it, the new file plays as long as the old one did, so the two never overlap.
    const oldFile = segsOf(d, ch)[index]?.file;
    const newMs = 'bedId' in source ? beds.data?.find((b) => b.id === source.bedId)?.durationMs : ownClips.find((c) => c.assetId === source.assetId)?.durationMs;
    const heard = oldFile ? segmentStretch(old, oldFile.durationMs) : null;
    const toMs = index < segs.length - 1 && heard && newMs ? Math.min(newMs, heard.toMs - heard.fromMs) : null;
    writeLayer(ch, segs.map((g, i) => (i === index ? fresh({ atMs: old.atMs + old.fromMs, toMs, gainDb: old.gainDb }) : g)));
  };
  /** The person most of the cut shows, whose voice the voiceover offers first. */
  // Add mode ends with Escape or a press anywhere outside its row.
  useEffect(() => {
    if (!adding) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setAdding(null); };
    const onAway = (e: PointerEvent) => { if (!(e.target as HTMLElement).closest(`.tint-${adding}`)) setAdding(null); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onAway);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onAway);
    };
  }, [adding]);
  /** The composition's own clips, each once, whose whole audio the voiceover can take. */
  const ownClips = (() => {
    const seen = new Map<string, { assetId: string; name: string; durationMs: number; person: string | null }>();
    for (const m of d?.slotMeta ?? []) if (m?.assetMs && !seen.has(m.assetId)) seen.set(m.assetId, { assetId: m.assetId, name: m.assetName, durationMs: m.assetMs, person: m.person });
    return [...seen.values()];
  })();
  /**
   * A full-length row (2026-10-04): its segments' fields, and in its toolbar Replace (the next best
   * candidate for the chosen segment) and ＋ (lay another segment after the last).
   */
  const layerRow = (ch: LaneChannel) => {
    if (!d) return null;
    const row = LAYER_ROWS[ch];
    const has = hasLayer(ch);
    const count = d.layers[ch].length;
    const index = segIndex(ch);
    const isAdding = adding === ch;
    const chosen = isAdding ? null : segsOf(d, ch)[index] ?? null;
    const seg = chosen?.seg ?? null;
    const gain = ch === 'music' ? d.musicGainDb : ch === 'voice' ? d.voiceGainDb : d.bedGainDb;
    const gainPatch = (db: number) => (ch === 'music' ? { musicGainDb: db } : ch === 'voice' ? { voiceGainDb: db } : { bedGainDb: db });
    // A new key per segment and for add mode, so the fields start from the segment they show.
    const pickerKey = `${ch}-${d.id}-${isAdding ? 'add' : index}`;
    const finding = replaceLayer.isPending && replaceLayer.variables?.channel === ch;
    const sub = finding ? 'Finding the next…'
      : isAdding ? `Adding ${row.unit.toLowerCase()} ${count + 1}`
        : layerNote?.ch === ch ? layerNote.text
          : count > 1 ? `${row.unit} ${index + 1} of ${count}` : row.sub;
    const whole = seg?.assetId && seg.fromMs === 0 && seg.toMs === null;
    return (
      <div className={`audio-row tint-${ch}${layerDrawn(ch) ? ' has-file' : ''}${laneShown(ch) ? ' lane-on' : ''}${!has || !channelHeard(ch, d.mutes, d.solos) ? ' muted' : ''}`}>
        <div className="compose-row-name">
          <b><span className="row-glyph" aria-hidden="true">{row.glyph}</span>{row.title}{laneChevron(ch)}</b>
          <span className="sub" role="status">{sub}</span>
        </div>
        <Toolbar dense>
          <Solo on={d.solos[ch]} label={row.name} disabled={!has} onToggle={() => setSolos({ [ch]: !d.solos[ch] })} />
          <Speaker muted={!has || d.mutes[ch]} silenced={has && silenced(ch)} label={row.name} disabled={!has} onToggle={() => setMutes({ [ch]: !d.mutes[ch] })} />
          {levelTool(ch, row.name, !has)}
          <Tool sparkle title={row.ask} active={ask?.channel === ch} onClick={(e) => openAsk(ch, e)} />
          <Tool icon="redo" title={`Replace ${row.unit.toLowerCase()} ${index + 1} with the next best`} disabled={!has || finding}
            onClick={() => {
              setAdding(null);
              replaceLayer.mutate({ draftId: d.id, channel: ch, index });
            }} />
          <Tool icon="trash" title={`Clear ${row.name}`} disabled={!has} onClick={() => clearChannel(ch)} />
          <Tool icon="plus" title={isAdding ? 'Stop adding' : `Add a ${row.unit.toLowerCase()} after the last`} active={isAdding} disabled={!has}
            onClick={() => setAdding(isAdding ? null : ch)} />
        </Toolbar>
        {ch === 'music' && <MusicPicker key={pickerKey} beds={beds.data ?? []} value={seg?.bedId ?? null} adding={isAdding} onPick={(id) => pickLayer(ch, id ? { bedId: id } : null)} />}
        {ch === 'soundtrack' && <SoundtrackPicker key={pickerKey} beds={beds.data ?? []} value={seg?.bedId ?? null} adding={isAdding} onPick={(id) => pickLayer(ch, id ? { bedId: id } : null)} />}
        {ch === 'voice' && (
          <VoicePicker key={pickerKey} beds={beds.data ?? []} clips={ownClips} onScreen={onScreenCreator} cutMs={totalMs} adding={isAdding}
            value={seg?.bedId ?? (whole ? `${WHOLE_CLIP}${seg.assetId}` : null)}
            clipLine={seg?.assetId && !whole ? `A line from ${chosen?.file?.name ?? 'a clip'} · ${fmtTime(seg.fromMs, false)} – ${fmtTime(seg.toMs ?? chosen?.file?.durationMs ?? 0, false)}` : null}
            onPick={(pick) => pickLayer(ch, pick)} />
        )}
        <div className="audio-tail">
          {channelKnob(ch, row.knob, gain, gainPatch, !has)}
          <DbMeter level={levelOf(ch)} />
        </div>
        {laneFor(ch)}
      </div>
    );
  };
  const onScreenCreator = (() => {
    const counts = new Map<string, number>();
    for (const m of d?.slotMeta ?? []) if (m?.person) counts.set(m.person, (counts.get(m.person) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
  })();
  const openAsk = (channel: AskChannel, e: React.MouseEvent<HTMLButtonElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    audioInstruct.reset();
    setAsk({ channel, x: Math.round(r.left), y: Math.round(r.bottom + 6) });
  };

  const pausePreview = () => haltPreview();
  /** Stop: pause and return the playhead to the very first frame of the cut. */
  const stopPreview = () => {
    const v = videoRef.current;
    if (!v || !d) return;
    haltPreview();
    cursorRef.current = 0;
    setPreviewSlot(null);
    showFront(0);
    if (previewUrl) {
      if (v.src !== mixUrl(previewUrl)) v.src = mixUrl(previewUrl);
      v.currentTime = 0;
    } else park(v, 0);
  };
  /** Write new slots straight into the cache; metaOrder permutes slotMeta alongside a reorder. */
  const applySlots = (slots: DraftSlot[], metaOrder?: number[]) => {
    if (!d) return;
    void queryClient.cancelQueries({ queryKey: trpc.compose.get.queryKey({ draftId: d.id }) });
    queryClient.setQueryData(trpc.compose.get.queryKey({ draftId: d.id }), (old: typeof d | undefined) => old ? {
      ...old,
      slots,
      slotMeta: metaOrder ? metaOrder.map((i) => old.slotMeta[i] ?? null) : old.slotMeta,
    } : old);
  };
  const setSlot = (index: number, patch: Partial<DraftSlot>) => {
    if (!d) return;
    const slots = d.slots.map((s, i) => (i === index ? { ...s, ...patch } : s));
    applySlots(slots);
    update.mutate({ draftId: d.id, slots });
  };
  /** Release commits instantly: the range goes into the cache before the server hears about it. */
  const commitSlot = (index: number, next: { inMs: number; outMs: number }) => {
    if (!d) return;
    const slots = d.slots.map((s, i) => (i === index ? { ...s, ...next } : s));
    applySlots(slots);
    setStagedSlots((s) => {
      const { [index]: gone, ...rest } = s;
      void gone;
      return rest;
    });
    update.mutate({ draftId: d.id, slots });
  };
  /**
   * A slip dragged on the card or the strip, held for both while it moves (clamped to the snippet's bounds),
   * then committed: the cache moves at once and the server lands it on whole frames (Dax, 2026-09-29).
   */
  const dragSlip = (index: number, deltaMs: number, done: boolean) => {
    if (!d) return;
    const slot = d.slots[index];
    const meta = d.slotMeta[index];
    if (!slot || !meta) return;
    const delta = clampSlip(deltaMs, slot, { inMs: meta.boundsInMs, outMs: meta.boundsOutMs });
    if (!done) {
      setSlipDrafts((sd) => (sd[index] === delta ? sd : { ...sd, [index]: delta }));
      return;
    }
    setSlipDrafts((sd) => {
      const { [index]: gone, ...rest } = sd;
      void gone;
      return rest;
    });
    if (!delta) return;
    applySlots(d.slots.map((s, i) => (i === index ? { ...s, inMs: s.inMs + delta, outMs: s.outMs + delta } : s)));
    slip.mutate({ draftId: d.id, slotIndex: index, deltaMs: delta });
  };
  /** A sound effect nudged on the ASMR strip: the card's own offset, clamped by the card's own rule. */
  const dragSound = (index: number, deltaMs: number, done: boolean) => {
    if (!d) return;
    const slot = d.slots[index];
    const sound = d.slotMeta[index]?.sound;
    if (!slot?.audio2 || !sound) return;
    const ms = clampSoundOffset(slot.audio2.offsetMs + deltaMs, sound.durationMs, slot.outMs - slot.inMs);
    if (!done) {
      setOffsetDrafts((o) => (o[index] === ms ? o : { ...o, [index]: ms }));
      return;
    }
    setOffsetDrafts((o) => {
      const { [index]: gone, ...rest } = o;
      void gone;
      return rest;
    });
    if (ms !== slot.audio2.offsetMs) setSlot(index, { audio2: { ...slot.audio2, offsetMs: ms } });
  };
  /** The drop commit: the visual order becomes the draft, per-index drafts reset with it. */
  const moveSlots = (perm: number[]) => {
    if (!d || perm.every((x, j) => x === j)) return;
    // Same-slot reuse warning: a clip moving into the sequence slot it last occupied in ANOTHER
    // composition gets a dated warning, always overridable.
    const clashes = perm.map((from, to) => ({ from, to }))
      .filter(({ from, to }) => from !== to)
      .map(({ from, to }) => ({ meta: d.slotMeta[from], to }))
      .filter((x) => x.meta && x.meta.lastUsedSlot === x.to && x.meta.lastUsedDraftId && x.meta.lastUsedDraftId !== d.id && x.meta.lastUsedAt);
    if (clashes.length) {
      const lines = clashes.map((c) => `"${c.meta!.title ?? c.meta!.role}" was used in sequence slot ${c.to + 1} on ${new Date(c.meta!.lastUsedAt!).toLocaleDateString()}`).join('\n');
      if (!window.confirm(`${lines}\n\nPlace it in the same sequence slot again anyway?`)) return;
    }
    const slots = perm.map((x) => d.slots[x]!);
    applySlots(slots, perm);
    setStagedSlots({});
    setOffsetDrafts({});
    update.mutate({ draftId: d.id, slots });
  };
  const removeSlot = (index: number) => {
    if (!d) return;
    const keep = d.slots.map((_, i) => i).filter((i) => i !== index);
    const slots = keep.map((i) => d.slots[i]!);
    applySlots(slots, keep);
    update.mutate({ draftId: d.id, slots });
  };
  /** Jitter: shift In and Out by up to six frames each way, clamped to the snippet's bounds. */
  // Client preview: two stacked players. The slot playing is in front; the next waits behind it,
  // parked on its first frame, and takes over at the out point. One player re-pointed at every slot
  // reloaded the file, even the same one, and flashed its poster between slots.
  // The watch is per presented frame (requestVideoFrameCallback), not per animation frame against the
  // clock: a clock check landed up to a display refresh late, after the first excluded frame was on
  // screen, and the swap waited a React render on top, so that frame flashed between scenes
  // (2026-10-03). The last kept frame now holds for its whole duration, longer if the next is late:
  // a held frame is invisible, a lost or foreign one is not.
  // A slot thumbnail plays only that slot; the preview's own play button runs the whole cut.
  const showFront = (i: 0 | 1) => {
    // The swap goes to the DOM at once; React's render catches up with the same classes after.
    const pair = [videoRef.current, altVideoRef.current];
    pair[i]?.classList.remove('standby');
    pair[i === 0 ? 1 : 0]?.classList.add('standby');
    frontRef.current = i;
    setFrontVideo(i);
  };
  /** Point a player at a slot's first frame, paused; the source only when it differs, so nothing reloads. */
  const park = (v: HTMLVideoElement, index: number, intoMs = 0) => {
    const proxy = d?.slotMeta[index]?.proxyUrl;
    const slot = d?.slots[index];
    if (!slot || !proxy) return;
    const url = mixUrl(proxy);
    if (v.src !== url) v.src = url;
    v.currentTime = (intoMs > 0 ? Math.min(slot.inMs + intoMs, slot.outMs - EDGE_INSET_MS) : insideEdge(slot.inMs)) / 1000;
  };
  /** Where the cut is: the playing slot's place on it, the rendered picture's time, or the parked playhead. */
  const positionMs = () => {
    if (!d || !mixPlan) return 0;
    if (previewUrl) {
      const v = videoRef.current;
      return v && v.src === mixUrl(previewUrl) ? v.currentTime * 1000 : cursorRef.current;
    }
    const i = playingSlotRef.current;
    const front = [videoRef.current, altVideoRef.current][frontRef.current];
    const slot = i === null ? undefined : d.slots[i];
    const span = i === null ? undefined : mixPlan.slots[i];
    if (!slot || !span || !front || (front.paused && !swappingRef.current)) return cursorRef.current;
    return span.startMs + Math.max(0, Math.min(slot.outMs - slot.inMs, front.currentTime * 1000 - slot.inMs));
  };
  /** The frame at a moment of the cut, shown paused. */
  const showFrameAt = (ms: number) => {
    const v = videoRef.current;
    if (!d || !mixPlan || !v) return;
    if (previewUrl) {
      if (v.src !== mixUrl(previewUrl)) v.src = mixUrl(previewUrl);
      v.currentTime = ms / 1000;
      return;
    }
    const index = slotAt(mixPlan, ms);
    const front = [videoRef.current, altVideoRef.current][frontRef.current];
    if (front) park(front, index, ms - mixPlan.slots[index]!.startMs);
  };
  /** The whole cut played from a moment of it. */
  const startPreviewAt = (ms: number) => {
    const front = [videoRef.current, altVideoRef.current][frontRef.current];
    if (!d || !mixPlan || !front) return;
    cursorRef.current = ms;
    if (previewUrl) {
      showFrameAt(ms);
      playPreview();
      return;
    }
    const index = slotAt(mixPlan, ms);
    const span = mixPlan.slots[index];
    if (!d.slotMeta[index]?.proxyUrl || !span) return;
    haltPreview();
    const m = wakeMix();
    park(front, index, ms - span.startMs);
    void m.start(mixPlan, ms, mixPlan.totalMs);
    playSlot(index, false);
  };
  /** The scrubber: the frame follows the drag; playback interrupted by it carries on where it is let go. */
  const seekPreview = (ms: number, done: boolean) => {
    if (!done) {
      if (previewPlaying) {
        resumeAfterSeek.current = true;
        haltPreview();
      }
      cursorRef.current = ms;
      showFrameAt(ms);
      return;
    }
    cursorRef.current = ms;
    if (resumeAfterSeek.current || previewPlaying) {
      resumeAfterSeek.current = false;
      startPreviewAt(ms);
    } else showFrameAt(ms);
  };
  const haltPreview = () => {
    // Paused mid-cut, the playhead stays where it was: play carries on from there.
    if (playingSlotRef.current !== null || previewUrl) cursorRef.current = positionMs();
    playingSlotRef.current = null;
    if (watchRef.current) watchRef.current.el.cancelVideoFrameCallback(watchRef.current.handle);
    watchRef.current = null;
    if (swapTimerRef.current !== null) window.clearTimeout(swapTimerRef.current);
    swapTimerRef.current = null;
    if (handOverRef.current) handOverRef.current.el.removeEventListener('seeked', handOverRef.current.fn);
    handOverRef.current = null;
    swappingRef.current = false;
    videoRef.current?.pause();
    altVideoRef.current?.pause();
    mixRef.current?.stop();
    setPreviewPlaying(false);
  };
  const playSlot = (index: number, only: boolean) => {
    const pair = [videoRef.current, altVideoRef.current];
    const front = pair[frontRef.current];
    const back = pair[frontRef.current === 0 ? 1 : 0];
    if (!d || !front || !back) return;
    setPreviewSlot(index);
    playingSlotRef.current = index;
    mixRef.current?.setElementGain(front, mixPlan?.slots[index]?.gainDb ?? null);
    void front.play();
    const next = !only && index + 1 < d.slots.length && d.slotMeta[index + 1]?.proxyUrl ? index + 1 : null;
    if (next !== null) park(back, next);
    const until = d.slots[index]!.outMs;
    let prevMs: number | null = null;
    /** The slot is over: stop here, or hand over once the next slot's first frame is ready behind. */
    const finish = () => {
      if (next === null) {
        // The end of the cut (or of the one clip played): the playhead parks where it stopped.
        cursorRef.current = positionMs();
        playingSlotRef.current = null;
        mixRef.current?.stop();
        front.pause();
        setPreviewSlot(null);
        return;
      }
      swappingRef.current = true;
      front.pause();
      const handOver = () => {
        handOverRef.current = null;
        showFront(frontRef.current === 0 ? 1 : 0);
        playSlot(next, false);
      };
      // The front holds its last frame until the next slot's first frame is ready behind it.
      if (back.seeking || back.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        handOverRef.current = { el: back, fn: handOver };
        back.addEventListener('seeked', handOver, { once: true });
      } else handOver();
    };
    const onFrame = (_now: number, meta: VideoFrameCallbackMetadata) => {
      watchRef.current = null;
      const presentedMs = meta.mediaTime * 1000;
      const frameMs = prevMs !== null && presentedMs > prevMs ? presentedMs - prevMs : null;
      prevMs = presentedMs;
      const verdict = frameAgainstEdge(presentedMs, frameMs, until);
      if (verdict === 'play') {
        watchRef.current = { el: front, handle: front.requestVideoFrameCallback(onFrame) };
        return;
      }
      if (verdict === 'past') {
        // A frame beyond the edge reached the screen: off it at once, and the stopped picture goes back
        // to the last kept frame.
        if (next === null) front.currentTime = (until - EDGE_INSET_MS) / 1000;
        finish();
        return;
      }
      // The last kept frame is showing: freeze on it, and let it run its full length before the swap.
      // Flagged first, so the pause reads as a swap, not as the viewer stopping the preview.
      if (next !== null) swappingRef.current = true;
      front.pause();
      swapTimerRef.current = window.setTimeout(() => {
        swapTimerRef.current = null;
        finish();
      }, Math.max(0, meta.expectedDisplayTime + frameMs! - performance.now()));
    };
    watchRef.current = { el: front, handle: front.requestVideoFrameCallback(onFrame) };
  };
  const startPreview = (index = 0, only = false) => {
    const front = [videoRef.current, altVideoRef.current][frontRef.current];
    const span = mixPlan?.slots[index];
    if (!d?.slotMeta[index]?.proxyUrl || !front || !mixPlan || !span) return;
    haltPreview();
    const m = wakeMix();
    park(front, index);
    // One clip plays with whatever else sounds under it: effects pushed in from its neighbours, the beds.
    void m.start(mixPlan, span.startMs, only ? span.endMs : mixPlan.totalMs);
    playSlot(index, only);
  };
  /**
   * Undo and redo (2026-10-04): every editable part of the draft, restored through the same save as any
   * edit. Slots come back with the refetch, which brings each clip's details with it.
   */
  const history = useDraftHistory({
    draft: d,
    snapshot: (x) => JSON.stringify({
      slots: x.slots, format: x.format, layers: x.layers, text: x.text, audioFx: x.audioFx, mutes: x.mutes, solos: x.solos,
      asmrTrim: x.asmrTrim, autoLevel: x.autoLevel, clipsGainDb: x.clipsGainDb, asmrGainDb: x.asmrGainDb, musicGainDb: x.musicGainDb,
      voiceGainDb: x.voiceGainDb, bedGainDb: x.bedGainDb, masterGainDb: x.masterGainDb,
    }),
    busy: () => queryClient.isMutating() > 0,
    restore: (snap) => {
      if (!d) return;
      const state = JSON.parse(snap) as Record<string, unknown> & { slots: DraftSlot[] };
      const { slots, ...rest } = state;
      patchDraft(rest);
      update.mutate({ draftId: d.id, slots, ...rest });
    },
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (!(e.metaKey || e.ctrlKey) || t?.closest('input, textarea, select, [contenteditable="true"]')) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) history.undo();
      else if ((key === 'z' && e.shiftKey) || key === 'y') history.redo();
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  const onVideoPlay = () => {
    swappingRef.current = false;
    setPreviewPlaying(true);
  };
  const onVideoPause = () => {
    if (swappingRef.current) return;
    mixRef.current?.stop();
    setPreviewPlaying(false);
  };

  /** The audio panel: one card, the same on every screen that edits a draft. */
  const audioDesk = () => {
    if (!d) return null;
    return (
              <div className="card audio-desk">
                <button type="button" className="section-head" aria-expanded={audioOpen} onClick={() => setAudioOpen(!audioOpen)}>
                  <span className="section-title">Audio</span>
                  {!audioOpen && <span className="sub section-hint">Click to expand</span>}
                  <Chevron up={audioOpen} />
                </button>
                {audioOpen && (
                  <>
                {/* Every row the same shape (Dax, 2026-09-30): its name with its glyph; one tight cluster of
                    speaker, sparkle, clear and import; fixed-width pickers; a knob and a meter. */}
                <div className={`audio-row tint-clip${laneShown('clips') ? ' lane-on' : ''}${!channelHeard('clips', d.mutes, d.solos) ? ' muted' : ''}`}>
                  <div className="compose-row-name">
                    <b><span className="row-glyph" aria-hidden="true">▥</span>Timeline volume{laneChevron('clips')}</b>
                    <span className="sub">Every clip&apos;s own sound</span>
                  </div>
                  <Toolbar dense>
                    <Solo on={d.solos.clips} label="every clip's own sound" onToggle={() => setSolos({ clips: !d.solos.clips })} />
                    <Speaker muted={d.mutes.clips} silenced={silenced('clips')} label="every clip's own sound" onToggle={() => setMutes({ clips: !d.mutes.clips })} />
                    {levelTool('clips', 'every clip\'s own sound', !d.slots.length)}
                    <Tool sparkle title="Ask about this channel" active={ask?.channel === 'clips'} onClick={(e) => openAsk('clips', e)} />
                  </Toolbar>
                  <div className="audio-tail">
                    {channelKnob('clips', 'Timeline volume', d.clipsGainDb, (db) => ({ clipsGainDb: db }), false)}
                    <DbMeter level={levelOf('clips')} />
                  </div>
                  {stripFor('clips')}
                </div>

                <div className={`audio-row tint-sfx${laneShown('asmr') ? ' lane-on' : ''}${!asmrOn || !channelHeard('asmr', d.mutes, d.solos) ? ' muted' : ''}`}>
                  <div className="compose-row-name">
                    <b><span className="row-glyph" aria-hidden="true"><Icon name="sfx" /></span>ASMR{laneChevron('asmr')}</b>
                    <span className="sub" role="status">{fillAudio.isPending ? 'Finding sounds…' : audioNote ?? 'A sound on every clip'}</span>
                  </div>
                  <Toolbar dense>
                    {/* Switching it on fetches a sound for every clip; after that the speaker only mutes. */}
                    <Solo on={d.solos.asmr} label="every clip's sound effect" disabled={!asmrOn} onToggle={() => setSolos({ asmr: !d.solos.asmr })} />
                    <Speaker muted={!asmrOn || d.mutes.asmr} silenced={asmrOn && silenced('asmr')} label={asmrOn ? 'every clip\'s sound effect' : 'ASMR: a sound for every clip'} disabled={fillAudio.isPending}
                      onToggle={() => {
                        if (!asmrOn) {
                          fillAudio.mutate({ draftId: d.id, prompt: null, onlyEmpty: true });
                          if (d.mutes.asmr) setMutes({ asmr: false });
                        } else setMutes({ asmr: !d.mutes.asmr });
                      }} />
                    {levelTool('asmr', 'every clip\'s sound effect', !asmrOn)}
                    <Tool sparkle title="Ask for sounds" active={ask?.channel === 'asmr'} onClick={(e) => openAsk('asmr', e)} />
                    <Tool icon="redo" title="Replace every clip's sound with a different one" disabled={!asmrOn || fillAudio.isPending} onClick={() => setReplaceAsk(true)} />
                    <Tool icon="trash" title="Clear every clip's sound" disabled={!asmrOn} onClick={() => clearChannel('asmr')} />
                    <Tool icon="fit" toggle="trim" pressed={d.asmrTrim} disabled={!asmrOn}
                      title={d.asmrTrim ? 'Trim clip: on, every sound stays inside its own clip. Click to play them whole' : 'Trim clip: hold every sound inside its own clip, fading out into each cut'}
                      onClick={toggleTrim} />
                  </Toolbar>
                  <div className="audio-tail">
                    {channelKnob('asmr', 'ASMR volume', d.asmrGainDb, (db) => ({ asmrGainDb: db }), !asmrOn)}
                    <DbMeter level={levelOf('asmr')} />
                  </div>
                  {stripFor('asmr')}
                </div>

                {layerRow('music')}
                {layerRow('voice')}
                {layerRow('soundtrack')}

                <div className="audio-row audio-master">
                  <div className="compose-row-name">
                    <b><span className="row-glyph" aria-hidden="true">∑</span>Master</b>
                    <span className="sub">All channels</span>
                  </div>
                  <label className="fx-box">
                    <input type="checkbox" checked={d.audioFx.normalise}
                      onChange={(e) => update.mutate({ draftId: d.id, audioFx: { ...d.audioFx, normalise: e.target.checked } })} />
                    <span>Normalise</span>
                  </label>
                  <div className="fx-box">
                    <label className="row fx-inner">
                      <input type="checkbox" checked={d.audioFx.compression !== 'off'}
                        onChange={(e) => update.mutate({ draftId: d.id, audioFx: { ...d.audioFx, compression: e.target.checked ? 'mid' : 'off' } })} />
                      <span>Compress</span>
                    </label>
                    {d.audioFx.compression !== 'off' && (
                      <span className="seg fx-mini-seg">
                        <button type="button" className={d.audioFx.compression !== 'high' ? 'on' : ''}
                          onClick={() => update.mutate({ draftId: d.id, audioFx: { ...d.audioFx, compression: 'mid' } })}>Medium</button>
                        <button type="button" className={d.audioFx.compression === 'high' ? 'on' : ''}
                          onClick={() => update.mutate({ draftId: d.id, audioFx: { ...d.audioFx, compression: 'high' } })}>High</button>
                      </span>
                    )}
                  </div>
                  <label className="fx-box">
                    <span>Bass</span>
                    <input type="range" className="gain fx-gain" min={-12} max={6} step={1} value={d.audioFx.bassDb} aria-label="Bass gain"
                      onChange={(e) => patchDraft({ audioFx: { ...d.audioFx, bassDb: Number(e.target.value) } })}
                      onPointerUp={(e) => update.mutate({ draftId: d.id, audioFx: { ...d.audioFx, bassDb: Number((e.target as HTMLInputElement).value) } })} />
                    <span className="sub mono">{d.audioFx.bassDb}</span>
                  </label>
                  <label className="fx-box">
                    <span>Treble</span>
                    <input type="range" className="gain fx-gain" min={-12} max={6} step={1} value={d.audioFx.trebleDb} aria-label="Treble gain"
                      onChange={(e) => patchDraft({ audioFx: { ...d.audioFx, trebleDb: Number(e.target.value) } })}
                      onPointerUp={(e) => update.mutate({ draftId: d.id, audioFx: { ...d.audioFx, trebleDb: Number((e.target as HTMLInputElement).value) } })} />
                    <span className="sub mono">{d.audioFx.trebleDb}</span>
                  </label>
                  <span className="audio-tail">
                    {masterWarn && !masterAuto && (
                      <button type="button" className="warn-tri" title={masterWarn.text} aria-label="Mix warning"
                        onClick={() => setWarnOpen(!warnOpen)}>
                        <Icon name="warn" />
                      </button>
                    )}
                    <Tool icon="level" toggle="level" pressed={masterAuto}
                      title={masterAuto ? 'Level the master: on, it keeps the whole mix level as sounds come and go. Click to hold it where it is' : 'Level the master: off. Click to level the whole mix now and as it changes'}
                      onClick={() => {
                        const autoLevel = { ...d.autoLevel, master: !masterAuto };
                        patchDraft({ autoLevel });
                        update.mutate({ draftId: d.id, autoLevel });
                        if (!masterAuto) void levelMaster();
                      }} />
                    <Knob label="Master volume" min={MASTER_GAIN_MIN_DB} max={MASTER_GAIN_MAX_DB} value={{ db: d.masterGainDb, muted: false }}
                      onChange={(v, done) => {
                        // Turned by hand, the master holds where it is put.
                        const patch = { masterGainDb: v.db, autoLevel: { ...d.autoLevel, master: false } };
                        patchDraft(patch);
                        if (done) {
                          update.mutate({ draftId: d.id, ...patch });
                          setMasterStale(true);
                        }
                      }} />
                    <DbMeter level={levelOf('master')} />
                  </span>
                  {warnOpen && masterWarn && (
                    <div className="warn-pop" role="dialog" aria-label="Mix warning">
                      <span className="sub">{masterWarn.text}</span>
                      <div className="row between">
                        <button type="button" className="btn quiet" onClick={() => setWarnOpen(false)}>Close</button>
                        <button type="button" className="btn primary" onClick={fixMaster}>Fix now</button>
                      </div>
                    </div>
                  )}
                </div>
                  </>
                )}
              </div>
    );
  };
  /** What the panel opens above the page: a channel's sparkle, and the two confirmations. */
  const audioPopups = () => (
    <>
        {d && ask && (
          <AskPop x={ask.x} y={ask.y}
            placeholder={ask.channel === 'clips' || ask.channel === 'master'
              ? 'e.g. check the volume and adjust it'
              : ask.channel === 'voice'
                ? 'e.g. a line with "sugar free", no longer than the cut'
                : ask.channel === 'asmr'
                  ? 'Find sound effects to match each scene\'s action'
                  : ask.channel === 'soundtrack'
                    ? 'e.g. a busy kitchen, all the way through'
                    : 'e.g. upbeat kitchen jingle, at least 10 seconds'}
            busy={audioInstruct.isPending || fillAudio.isPending}
            // The ASMR default needs no words (Dax, 2026-10-01): Auto, or Go on an empty box, gives every clip
            // the effect its own indexed words find, replacing what is there; the row's status reports it.
            auto={ask.channel === 'asmr' ? {
              title: 'Find matching sound effects',
              run: () => {
                fillAudio.mutate({ draftId: d.id, prompt: null, onlyEmpty: false });
                if (d.mutes.asmr) setMutes({ asmr: false });
                setAsk(null);
              },
            } : undefined}
            note={audioInstruct.data?.note ?? (audioInstruct.isError ? audioInstruct.error.message : null)}
            onSubmit={(text) => audioInstruct.mutate({ draftId: d.id, channel: ask.channel, instruction: text })}
            onClose={() => { setAsk(null); audioInstruct.reset(); }} />
        )}
        {d && clearAsk !== null && (
          <>
            <button type="button" className="overlay overlay-btn" aria-label="Cancel" onClick={() => setClearAsk(null)} />
            <div className="modal confirm-modal" role="alertdialog" aria-label="Clear every clip's sound">
              <h2>Clear every clip&apos;s sound?</h2>
              <span className="sub">The sound comes off all {clearAsk} clips. The ASMR row stays; switch it on again to fetch new ones.</span>
              <div className="row between">
                <button type="button" className="btn quiet" onClick={() => setClearAsk(null)}>Cancel</button>
                <button type="button" className="btn primary" onClick={() => { setClearAsk(null); clearChannel('asmr', true); }}>Clear {clearAsk} sounds</button>
              </div>
            </div>
          </>
        )}
        {d && replaceAsk && (
          <>
            <button type="button" className="overlay overlay-btn" aria-label="Cancel" onClick={() => setReplaceAsk(false)} />
            <div className="modal confirm-modal" role="alertdialog" aria-label="Replace every clip's sound">
              <h2>Replace every clip&apos;s sound?</h2>
              <span className="sub">This will replace all clips&apos; sounds, a different one on each.</span>
              <div className="row between">
                <button type="button" className="btn quiet" onClick={() => setReplaceAsk(false)}>Cancel</button>
                <button type="button" className="btn primary" onClick={() => { setReplaceAsk(false); fillAudio.mutate({ draftId: d.id, prompt: null, replace: true }); }}>Replace all</button>
              </div>
            </div>
          </>
        )}
    </>
  );

  return { renderId, setRenderId, previewUrl, setPreviewUrl, audioNote, setAudioNote, previewPlaying, setPreviewPlaying, stagedSlots, setStagedSlots, activeEdge, setActiveEdge, offsetDrafts, setOffsetDrafts, slipDrafts, setSlipDrafts, laneOpen, setLaneOpen, clearAsk, setClearAsk, replaceAsk, setReplaceAsk, selectedSound, setSelectedSound, selectedLayer, setSelectedLayer, trimDrafts, setTrimDrafts, segPick, setSegPick, adding, setAdding, segDraft, setSegDraft, layerNote, setLayerNote, liveStats, setLiveStats, mixRef, mix, audioOpen, setAudioOpen, ask, setAsk, warnOpen, setWarnOpen, previewSlot, setPreviewSlot, videoRef, altVideoRef, frontRef, frontVideo, setFrontVideo, watchRef, swapTimerRef, handOverRef, swappingRef, cursorRef, playingSlotRef, resumeAfterSeek, draft, beds, render, refresh, update, updating, draftSave, slip, fillAudio, refreshAudio, audioInstruct, replaceLayer, d, totalMs, asmrOn, segsOf, segIndex, slotCount, clipTints, planOf, mixPlan, exportStats, levelOf, masterStale, setMasterStale, masterAuto, setMaster, masterWarn, fixMaster, passHeard, wakeMix, playPreview, patchDraft, setSolos, silenced, panelNote, setMutes, hasLayer, layerDrawn, laneReady, laneShown, laneChevron, stripFor, writeLayer, dragSegment, trimSegment, laneFor, trimSound, clampedSlots, toggleTrim, levelOffByHand, offByHand, channelKnob, levelling, setLevelling, latestDraft, gainRange, soundStretch, levelSounds, levelClips, levelLayer, levelChannel, levelTool, levelSeen, levelFresh, skipSoundSilence, skipLeadSilence, masterStems, mixKey, levelMaster, masterSeen, masterTimer, clearChannel, pickLayer, ownClips, layerRow, onScreenCreator, openAsk, pausePreview, stopPreview, applySlots, setSlot, commitSlot, dragSlip, dragSound, moveSlots, removeSlot, showFront, park, positionMs, showFrameAt, startPreviewAt, seekPreview, haltPreview, playSlot, startPreview, history, onVideoPlay, onVideoPause, audioDesk, audioPopups };
}

/** One channel's measured level: a mean-filled bar with a sticky peak tick, scale -40 to 0 dB. */
function DbMeter({ level }: { level: ChannelLevel | null }) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const pct = (db: number) => `${Math.max(0, Math.min(100, ((db + 40) / 40) * 100)).toFixed(1)}%`;
    el.style.setProperty('--mean', level ? pct(level.meanDb) : '0%');
    el.style.setProperty('--peak', level ? pct(level.peakDb) : '0%');
  }, [level]);
  return (
    <span ref={ref} className={`db-meter${level && level.peakDb >= CLIPPING_PEAK_DB ? ' clip' : ''}`}
      title={level ? `mean ${level.meanDb.toFixed(1)} dB · peak ${level.peakDb.toFixed(1)} dB` : 'Measured while the preview plays'}>
      {/* The peak reads small above the bar, so the meter takes no more width than its bar (Dax, 2026-09-30). */}
      <span className="sub mono db-read">{level ? level.peakDb.toFixed(1) : '–'}</span>
      <span className="db-scale" aria-hidden="true"><span className="db-fill" /><span className="db-tick" /></span>
    </span>
  );
}

/** The channel sparkle's floating prompt, anchored where the user clicked; the note stays until closed. */
function AskPop({ x, y, placeholder, busy, auto, note, onSubmit, onClose }: {
  x: number;
  y: number;
  placeholder: string;
  busy: boolean;
  /** A channel with a default instruction: an Auto button runs it, and so does Go on an empty box. */
  auto?: { title: string; run: () => void } | undefined;
  note: string | null;
  onSubmit: (text: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [text, setText] = useState('');
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty('--pop-x', `${Math.round(Math.max(8, Math.min(x, window.innerWidth - el.offsetWidth - 8)))}px`);
    el.style.setProperty('--pop-y', `${Math.round(Math.max(8, Math.min(y, window.innerHeight - el.offsetHeight - 8)))}px`);
  }, [x, y, note, busy]);
  return createPortal(
    <>
      <button type="button" className="overlay overlay-btn overlay-clear" aria-label="Close" onClick={onClose} />
      <div ref={ref} className="ask-pop" role="dialog" aria-label="Ask about this channel">
        <form className="row ask-pop-row" onSubmit={(e) => {
          e.preventDefault();
          if (busy) return;
          if (text.trim().length >= 3) onSubmit(text.trim());
          else if (auto && !text.trim()) auto.run();
        }}>
          <span className="ask-spark" aria-hidden="true"><Spark /></span>
          <input autoFocus value={text} placeholder={placeholder} aria-label="Instruction" maxLength={400}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }} />
          {auto && <button type="button" className="btn quiet" title={auto.title} disabled={busy} onClick={auto.run}>Auto</button>}
          <button type="submit" className="btn primary" disabled={busy || (auto && !text.trim() ? false : text.trim().length < 3)}>{busy ? 'Working…' : 'Go'}</button>
        </form>
        {note && <div className="sub ask-pop-note" role="status">{note}</div>}
      </div>
    </>,
    document.body,
  );
}
